import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    useTranslator,
    useCurrentLanguage,
    useUrlLocalizer
} from 'tradera-lang/translate';
import {
    fetchCartData,
    removeItemsFromCart,
    setCartItemQuantity,
    confirmCartPurchase,
    createUncommittedPurchaseOrder
} from 'tradera-state/views/cart/actions';
import cookieUtil from 'tradera-utils/cookie';
import SiteSubheader from 'tradera-components/site-subheader/site-subheader';
import Breadcrumbs from 'tradera-components/breadcrumbs/breadcrumbs';
import { formatPrice } from '@tradera/blueprint/utils';
import { LocalLoader } from 'tradera-components/loader';
import { getShippingDisplayText } from 'tradera-helpers/shipping-display-text';

import TopDescription from '../components/vip/bidding/bin-modal/bin-modal-top-description';
import SellerName from '../components/vip/bidding/bin-modal/bin-modal-seller-name';
import ItemDisplay from '../components/vip/bidding/bin-modal/bin-modal-item-display';
import ShippingDisplay from '../components/vip/bidding/bin-modal/bin-modal-shipping-display';
import PaymentSummary from '../components/vip/bidding/bin-modal/bin-modal-payment-summary';
import BottomButtons from '../components/vip/bidding/bin-modal/bin-modal-bottom-buttons';
import {
    Separator,
    BigSeparator
} from '../components/vip/bidding/bin-modal/bin-modal';
import { CART_ID } from 'tradera-constants/cookies';
import uiActions from 'tradera-state/ui/actions';
import { selectMemberId } from 'tradera-state/member/selectors';
import { BID_VALIDATION_RESULTS } from '../components/vip/api/vip-api-constants';
import './cart-page.scss';

const mapShippingOption = (ship, t) => {
    return {
        displayText: getShippingDisplayText(ship.shippingType, t),
        cost: ship.shippingCost,
        toCountryCodeIso2: ship.toCountryCode
    };
};

const CartPage = ({ history, location }) => {
    const cartId = cookieUtil.readCookie(CART_ID);
    const fromAddItem =
        new URLSearchParams(location.search).get('source') === 'addItem';
    const dispatch = useDispatch();
    const { t } = useTranslator();
    const { toLocalizedUrl } = useUrlLocalizer();

    const { language } = useCurrentLanguage();

    const memberId = useSelector(selectMemberId);
    const { isFetching, merchantPreOrders, errors } = useSelector(
        state => state.views.cart
    );
    const preferredCurrency = useSelector(
        state => state.multiCurrency.preferredCurrency
    );
    const buyerCountryCode = useSelector(
        state => state.shippingRegion.shippingCountry.countryCodeIso2
    );

    useEffect(() => {
        dispatch(fetchCartData(cartId));
    }, [dispatch, cartId]);

    const goToSeller = (merchantId, cleanedAlias) => {
        window.location = toLocalizedUrl(
            `/profile/items/${merchantId}/${cleanedAlias}`
        );
    };

    const onConfirm = order => {
        if (!memberId) {
            return dispatch(
                uiActions.signIn({ redirectUrl: '/shopping/cart' })
            );
        }

        const shopItems = order.orderRows.map(item => {
            return {
                itemId: item.itemId,
                expectedItemPrice: item.unitPrice,
                quantity: item.quantity
            };
        });
        if (order.isSellpyCheckout) {
            dispatch(
                confirmCartPurchase(
                    cartId,
                    order.id,
                    shopItems,
                    transactionId => {
                        window.location = `/Shopping/Summary/Index?transactionId=${transactionId}`;
                    }
                )
            );
        } else if (order.isCommitOnPayment) {
            dispatch(
                createUncommittedPurchaseOrder(
                    cartId,
                    order.id,
                    order.merchantMemberId,
                    shopItems,
                    purchaseOrderId => {
                        window.location = `/Shopping/Checkout/SelectPayment?purchaseOrderId=${purchaseOrderId}`;
                    }
                )
            );
        } else {
            dispatch(
                confirmCartPurchase(
                    cartId,
                    order.id,
                    shopItems,
                    transactionId => {
                        window.location = `/Shopping/Checkout/SelectPaymentForTransaction?transactionId=${transactionId}`;
                    }
                )
            );
        }
    };

    const isCheckoutDisabled = order => {
        return (
            order.orderRows.some(
                row =>
                    (row.quantityInShop !== undefined &&
                        row.quantity > row.quantityInShop) ||
                    row.isUnavailable
            ) || errors[order.id]
        );
    };

    const changeItemQuantity = (itemId, quantity) => {
        if (quantity === 0) {
            dispatch(removeItemsFromCart(cartId, [itemId]));
        } else {
            dispatch(setCartItemQuantity(cartId, itemId, quantity));
        }
    };

    return (
        <div className="cartoverview-page text-inter text-dark">
            <SiteSubheader className="px-md-2">
                <Breadcrumbs
                    categoryPath={[
                        {
                            id: 1,
                            name: t('cart_breadcrumb_title'),
                            url: location.pathname
                        }
                    ]}
                    inSPA={true}
                />
            </SiteSubheader>
            <div className="site-width pt-2 pt-md-4 position-relative">
                {isFetching && <LocalLoader className="py-4" />}

                {!isFetching && fromAddItem && (
                    <div
                        className="mx-md-auto px-2 px-md-4"
                        style={{
                            maxWidth: '64.28571rem',
                            zIndex: 1
                        }}>
                        <button
                            className="d-md-none btn btn-outline-primary btn-md w-100 mb-2"
                            onClick={history.goBack}>
                            &lt;- {t('checkout_bin_button1')}
                        </button>
                        <button
                            className="d-none d-md-inline-block btn btn-outline-primary position-absolute z-default"
                            onClick={history.goBack}>
                            &lt;- {t('checkout_bin_button1')}
                        </button>
                    </div>
                )}

                {!isFetching && merchantPreOrders.length === 0 && (
                    <div className="w-100 py-4 text-center">
                        {t('cartCheckoutOverview_cartEmpty')}
                    </div>
                )}

                {merchantPreOrders.length > 0 && (
                    <div className="w-100 px-2 px-md-4 pb-2 pb-md-4">
                        <TopDescription
                            titleKey={'checkout_bin_title'}
                            descriptions={[
                                'checkout_bin_description1',
                                'checkout_bin_description2'
                            ]}
                            classes="mb-2"
                        />
                    </div>
                )}

                {merchantPreOrders.map(order => {
                    const shippingOptions = order.shippingOptions.map(option =>
                        mapShippingOption(option, t)
                    );
                    const totalPrice = order.orderRows.reduce(
                        (sum, row) => sum + row.unitPrice * row.quantity,
                        0
                    );
                    const shippingPriceRange =
                        order.isSellpyCheckout && order.shippingPriceRange
                            ? {
                                  minPrice: order.shippingPriceRange.minPrice,
                                  maxPrice: Math.min(
                                      order.shippingPriceRange.maxPrice,
                                      99
                                  )
                              }
                            : order.shippingPriceRange;
                    return (
                        <div
                            className="container seller-summary justify-content-center px-2 px-md-4 border gray-300"
                            key={order.id}>
                            <SellerName
                                memberId={order.merchantMemberId}
                                alias={order.merchantAlias}
                                cleanedAlias={order.cleanedAlias}
                                hasBackground={true}
                                hasShop={true}
                            />

                            {order.orderRows.map((row, index) => (
                                <ItemDisplay
                                    key={row.itemId}
                                    itemImage={row.imageUrl}
                                    itemTitle={row.title}
                                    unitPrice={row.unitPrice}
                                    language={language}
                                    isFirstItem={index === 0}
                                    quantity={row.quantity}
                                    quantityInShop={row.quantityInShop}
                                    onQuantityChange={quantity =>
                                        changeItemQuantity(
                                            row.itemId,
                                            parseInt(quantity)
                                        )
                                    }
                                    itemUrl={toLocalizedUrl(row.itemUrl)}
                                    isUnavailable={row.isUnavailable}
                                />
                            ))}

                            <Separator />
                            <ShippingDisplay
                                shippingOptions={shippingOptions}
                                shippingPriceRange={shippingPriceRange}
                                buyerCountryCode={buyerCountryCode}
                                isSellpyCheckout={order.isSellpyCheckout}
                            />

                            <Separator />

                            <PaymentSummary
                                formattedPrice={formatPrice(
                                    totalPrice,
                                    language,
                                    preferredCurrency
                                )}
                                shippingOptions={shippingOptions}
                                isSellpyCheckout={order.isSellpyCheckout}
                                totalPrice={totalPrice}
                            />

                            <BigSeparator />

                            <BottomButtons
                                leftButton={{
                                    action: () =>
                                        goToSeller(
                                            order.merchantMemberId,
                                            order.cleanedAlias
                                        ),
                                    text: t('checkout_bin_keepShopping', {
                                        seller: order.merchantAlias
                                    }),
                                    className: 'btn-primary'
                                }}
                                rightButton={{
                                    action: () => onConfirm(order),
                                    text:
                                        !order.isCommitOnPayment ||
                                        order.isSellpyCheckout
                                            ? t('checkout_bin_title')
                                            : t('cart_offplatform_continue'),
                                    className: 'btn-success',
                                    disabled: isCheckoutDisabled(order)
                                }}
                            />

                            {errors && errors[order.id] && (
                                <CartError error={errors[order.id]} />
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

const CartError = ({ error }) => {
    const { t } = useTranslator();

    const mapErrorCode = errorCode => {
        switch (errorCode) {
            case BID_VALIDATION_RESULTS.BIDDER_IS_BLOCKED:
                return 'checkout_item_bidderIsBlocked';
            default:
                return 'syi_publish_error_unknownerror';
        }
    };
    const errorMessage = mapErrorCode(error);

    return (
        <div className="row">
            <div className="col-12 d-flex justify-content-center mb-2">
                <h2 className="text-danger text-styled">{t(errorMessage)}</h2>
            </div>
        </div>
    );
};

export default CartPage;
